import type { KeyboardEvent, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { defineMessages, useIntl } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { WidthObserver } from '@atlaskit/width-detector/width-observer';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import IconButton from '@atlaskit/button/icon/button';
import { cx, cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import DragHandlerIcon from '@atlaskit/icon/core/drag-handle-horizontal';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import {
	draggable,
	dropTargetForElements,
	monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/utils/set-custom-native-drag-preview';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/utils/pointer-outside-of-preview';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import type { TabOrientation, TabParameters } from '../nativeTabsTypes.ts';
import type { TabColorKey } from '../tab-color/tabColorDefinitions.ts';
import { NATIVE_TAB_TYPE } from '../utils/dragDropUtils.ts';
import { getTabAxis } from '../utils/tabOrientation.ts';

import { TabDragPreview } from './TabDragPreview.tsx';
import { TabPill } from './TabPill.tsx';
import { TabPillMenu } from './TabPillMenu.tsx';

const styles = cssMap({
	// Hides the element visually while keeping it accessible to screen readers.
	// Used for the hidden role="tablist" div that owns the tab IDs via aria-owns.
	srOnly: {
		position: 'absolute',
		width: '1px',
		height: '1px',
		padding: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		borderWidth: 0,
	},
	// Fused right-side visual container for the split-button ellipsis trigger.
	// Matches the active TabPill's border and background so the two elements
	// appear as one cohesive pill to the user, while remaining separate DOM
	// elements (required for ARIA compliance — tablist cannot contain
	// non-tab interactive elements).
	menuTriggerFused: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
	},
	overflowVisible: {
		overflow: 'visible',
	},
	scrollContainer: {
		overflowX: 'auto',
		overflowY: 'visible',
		scrollbarWidth: 'none',
		display: 'flex',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
	},
	tabBarRow: {
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'row',
		width: '100%',
		columnGap: token('space.100'),
		overflow: 'visible',
	},
	tabBarColumn: {
		display: 'flex',
		alignItems: 'stretch',
		flexDirection: 'column',
		flexShrink: 0,
		width: 'fit-content',
		maxWidth: '304px',
		overflow: 'visible',
	},
	scrollContainerVertical: {
		display: 'flex',
		flexDirection: 'column',
		// Overflow depends on rendered height, including wrapped titles and gaps.
		maxHeight: '404px',
		overflowX: 'visible',
		overflowY: 'auto',
	},
	scrollContainerWrapperVertical: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		overflow: 'visible',
	},
	tabPillItemVertical: {
		flexShrink: 0,
		position: 'relative',
		overflow: 'visible',
	},
	tabItems: {
		display: 'flex',
		alignItems: 'center',
		columnGap: token('space.100'),
		flexDirection: 'row',
		flexWrap: 'nowrap',
	},
	tabItemsVertical: {
		alignItems: 'flex-start',
		rowGap: token('space.100'),
		columnGap: token('space.0'),
		flexDirection: 'column',
	},
	addTabButton: {
		marginBlockStart: token('space.025'),
	},
	addTabButtonVertical: {
		alignSelf: 'flex-end',
	},
	tabPillItem: {
		flexShrink: 0,
		position: 'relative',
		overflow: 'visible',
	},
	tabPillWrapper: {
		position: 'relative',
		display: 'flex',
		alignItems: 'stretch',
		overflow: 'visible',
	},
	terminalDropPlaceholder: {
		display: 'flex',
		alignItems: 'stretch',
		flexShrink: 0,
		width: '8px',
	},
	terminalDropIndicatorAnchor: {
		position: 'relative',
		flexShrink: 0,
		width: 0,
		marginInlineStart: token('space.050'),
	},
	dragBar: {
		position: 'absolute',
		top: token('space.0'),
		left: '50%',
		transform: 'translate(-50%, -50%)',
		borderRadius: token('radius.small'),
		transition: 'width 0.1s ease, height 0.1s ease, background-color 0.1s ease',
		cursor: 'grab',
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		border: 'none',
		padding: token('space.0'),
		color: token('color.text.selected'),
		width: '24px',
		height: '4px',
		backgroundColor: token('color.background.accent.gray.subtle'),
		opacity: 0,
	},
	navButton: {
		marginBlockStart: token('space.025'),
	},
	verticalNavGroup: {
		display: 'flex',
		alignSelf: 'flex-end',
		flexDirection: 'column',
		rowGap: token('space.050'),
		marginBlockStart: token('space.025'),
	},
	// Fused menu trigger shape for colored tabs (bg/border applied separately per color)
	menuTriggerFusedColored: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
	},
	scrollContainerWrapper: {
		position: 'relative',
		display: 'flex',
		flex: '0 1 auto',
		minWidth: 0,
		overflow: 'visible',
		marginInlineStart: token('space.negative.050'),
		marginInlineEnd: token('space.negative.050'),
	},
	scrollFadeLeft: {
		position: 'absolute',
		left: 0,
		top: 0,
		bottom: 0,
		width: '24px',
		backgroundImage: `linear-gradient(to left, transparent, ${token('elevation.surface')})`,
		pointerEvents: 'none',
		zIndex: 1,
	},
	scrollFadeRight: {
		position: 'absolute',
		right: 0,
		top: 0,
		bottom: 0,
		width: '24px',
		backgroundImage: `linear-gradient(to right, transparent, ${token('elevation.surface')})`,
		pointerEvents: 'none',
		zIndex: 1,
	},
	// Vertical counterparts of the horizontal edge fades, so tabs scrolling past the top or
	// bottom of the list fade out instead of being cut off abruptly.
	scrollFadeTop: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		height: '24px',
		backgroundImage: `linear-gradient(to top, transparent, ${token('elevation.surface')})`,
		pointerEvents: 'none',
		zIndex: 1,
	},
	scrollFadeBottom: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
		height: '24px',
		backgroundImage: `linear-gradient(to bottom, transparent, ${token('elevation.surface')})`,
		pointerEvents: 'none',
		zIndex: 1,
	},
});

type DragState =
	| { type: 'idle' }
	| { type: 'dragging' }
	| { type: 'generate-preview'; container: HTMLElement };

const idle: DragState = { type: 'idle' };

const i18n = defineMessages({
	tablistLabel: {
		id: 'native-tabs.tab-bar.tablist-label.non-final',
		defaultMessage: 'Tabs',
		description: 'Accessible label for the Native Tabs navigation (the WAI-ARIA tablist).',
	},
	addTab: {
		id: 'native-tabs.tab-bar.add-tab-label.non-final',
		defaultMessage: 'Add tab',
		description: 'Label for the icon-only "+" button that appends a new tab.',
	},
	scrollTabsLeft: {
		id: 'native-tabs.tab-bar.scroll-tabs-left.non-final',
		defaultMessage: 'Scroll tabs left',
		description: 'Accessible label for the button that scrolls the tab bar to the left.',
	},
	scrollTabsRight: {
		id: 'native-tabs.tab-bar.scroll-tabs-right.non-final',
		defaultMessage: 'Scroll tabs right',
		description: 'Accessible label for the button that scrolls the tab bar to the right.',
	},
	scrollTabsUp: {
		id: 'native-tabs.tab-bar.scroll-tabs-up.non-final',
		defaultMessage: 'Scroll tabs up',
		description: 'Accessible label for the button that scrolls the vertical tab list up.',
	},
	scrollTabsDown: {
		id: 'native-tabs.tab-bar.scroll-tabs-down.non-final',
		defaultMessage: 'Scroll tabs down',
		description: 'Accessible label for the button that scrolls the vertical tab list down.',
	},
});

export type TabBarTab = TabParameters;

export type TabBarProps = {
	/** Ordered list of tabs to render. */
	tabs: ReadonlyArray<TabBarTab>;
	/** 0-based index of the active tab. */
	activeIndex: number;
	/** Called when a tab is selected (click or keyboard nav). Receives the new index. */
	onTabClick: (index: number) => void;
	/** Called when the Add tab button is clicked. Required when `canEdit` is true. */
	onAddTab?: () => void;
	/** Whether editing controls (add tab button, ellipsis menu) are shown. */
	canEdit: boolean;
	/** Tab navigation orientation. */
	orientation?: TabOrientation;
	/** Called when the active tab title is renamed. Only provided in edit mode. */
	onRenameTab?: (index: number, title: string) => void;
	/** Called when the user clicks "Add emoji" or the emoji on an active tab. */
	onEmojiPickerOpen?: () => void;
	/** Called when the user clicks "Remove emoji". */
	onRemoveEmoji?: () => void;
	/** Whether the emoji picker popup is open. */
	isEmojiPickerOpen?: boolean;
	/** Called when the emoji picker popup should close. */
	onEmojiPickerClose?: () => void;
	/** The React node representing the emoji picker content. */
	emojiPickerContent?: ReactNode;
	/** Called when a tab is dragged to a new position. Only provided in edit mode. */
	onReorderTabs?: (sourceIndex: number, targetIndex: number, edge: Edge) => void;
	/** Called when the user clicks "Move tab left" or triggers the keyboard shortcut. */
	onMoveTabPrevious?: () => void;
	/** Called when the user clicks "Move tab right" or triggers the keyboard shortcut. */
	onMoveTabNext?: () => void;
	/** Called when the user clicks "Copy link to tab". */
	onCopyLink?: () => void;
	/** Called when the user deletes a tab from the context menu. */
	onDeleteTab?: (index: number) => void;
	/** Emoji provider for resolving emoji images in tab pills. */
	emojiProvider?: Promise<EmojiProvider>;
	/** Called when the user selects a color for the active tab. */
	onColorChange?: (color: TabColorKey | undefined) => void;
	/** Whether "update all tab colors" mode is active. */
	applyToAll: boolean;
	/** Called when the applyToAll toggle changes. */
	onApplyToAllChange?: (value: boolean) => void;
	/**
	 * Drop indicator state from the expanded content-area drop target in NativeTabs.
	 * When set, the cursor is over the content panel (not a pill) during a drag,
	 * and the indicator should render at this pill index + edge.
	 */
	contentAreaDrop?: { index: number; edge: Edge } | null;
	/**
	 * Stable instance identity shared between NativeTabs and TabBar to scope
	 * drag events to a single tabs macro instance.
	 */
	instanceId?: object;
	/** Callback to register pill wrapper elements for expanded drop detection. */
	registerPillWrapper?: (index: number, element: HTMLElement | null) => void;
};

type TabBarItemProps = {
	tab: TabBarTab;
	index: number;
	isActive: boolean;
	showMenuButton: boolean;
	canDrag: boolean;
	orientation: TabOrientation;
	registerRef: (index: number, element: HTMLButtonElement | null) => void;
	registerMenuRef: (index: number, element: HTMLDivElement | null) => void;
	onSelect: (index: number) => void;
	onEmojiPickerOpen?: () => void;
	onRemoveEmoji?: () => void;
	isEmojiPickerOpen?: boolean;
	onEmojiPickerClose?: () => void;
	emojiPickerContent?: ReactNode;
	emojiProvider?: Promise<EmojiProvider>;
	onMoveTabPrevious?: () => void;
	onMoveTabNext?: () => void;
	onCopyLink?: () => void;
	onDeleteTab?: (index: number) => void;
	canRename: boolean;
	editingTabId: string | null;
	onTitleEditStart: (index: number) => void;
	onTitleEditSave: (
		index: number,
		title: string,
		options?: { shouldRestoreFocus?: boolean; focusVisible?: boolean },
	) => void;
	onTitleEditCancel: (index: number, options?: { focusVisible?: boolean }) => void;
	isFirst: boolean;
	isLast: boolean;
	isDeletePreviewed: boolean;
	onDeletePreviewStart: (index: number) => void;
	onDeletePreviewEnd: () => void;
	scrollContainerRef: React.RefObject<HTMLDivElement>;
	/** Stable instance identity from NativeTabs for scoping drag events. */
	externalInstanceId?: object;
	/** Callback to register this pill's wrapper element for expanded drop detection. */
	registerPillWrapper?: (index: number, element: HTMLElement | null) => void;
	onColorChange?: (color: TabColorKey | undefined) => void;
	applyToAll: boolean;
	onApplyToAllChange?: (value: boolean) => void;
	/** Drop edge from the expanded content-area target (when cursor is below the header). */
	contentAreaEdge?: Edge | null;
};

const TabBarItem = ({
	tab,
	index,
	isActive,
	showMenuButton,
	canDrag,
	orientation,
	registerRef,
	registerMenuRef,
	onSelect,
	onEmojiPickerOpen,
	onRemoveEmoji,
	isEmojiPickerOpen = false,
	onEmojiPickerClose,
	emojiPickerContent = null,
	emojiProvider,
	onMoveTabPrevious,
	onMoveTabNext,
	onCopyLink,
	onDeleteTab,
	canRename,
	editingTabId,
	onTitleEditStart,
	onTitleEditSave,
	onTitleEditCancel,
	isFirst,
	isLast,
	isDeletePreviewed,
	onDeletePreviewStart,
	onDeletePreviewEnd,
	scrollContainerRef,
	externalInstanceId,
	registerPillWrapper,
	onColorChange,
	applyToAll,
	onApplyToAllChange,
	contentAreaEdge,
}: TabBarItemProps) => {
	const axis = getTabAxis(orientation);
	const [dragState, setDragState] = useState<DragState>(idle);
	const [pillEdge, setPillEdge] = useState<Edge | null>(null);
	const [isHovered, setIsHovered] = useState(false);
	const dragBarRef = useRef<HTMLButtonElement>(null);
	const [isBarHovered, setIsBarHovered] = useState(false);
	const [optimisticColor, setOptimisticColor] = useState<TabColorKey | undefined>(tab.color);

	// Merge pill-level edge (cursor over this pill) with content-area edge (cursor below header).
	// Pill-level takes priority when set.
	const closestEdge = pillEdge ?? contentAreaEdge ?? null;

	// Sync optimisticColor when tab.color is updated externally (e.g. applyToAll from another tab).
	useEffect(() => {
		setOptimisticColor(tab.color);
	}, [tab.color]);

	const wrapperRef = useRef<HTMLDivElement>(null);

	// Register this pill's wrapper element for expanded drop detection.
	useEffect(() => {
		registerPillWrapper?.(index, wrapperRef.current);
		return () => registerPillWrapper?.(index, null);
	}, [index, registerPillWrapper]);

	const handleRef = (element: HTMLButtonElement | null) => {
		registerRef(index, element);
	};
	const handleMenuRef = (element: HTMLDivElement | null) => {
		registerMenuRef(index, element);
	};
	const handleClick = () => {
		onSelect(index);
	};

	// Restrict effectAllowed to 'move' to prevent the browser from showing a
	// green "+" (copy) cursor inside contenteditable areas.
	const handleDragStart = useCallback((e: React.DragEvent) => {
		e.dataTransfer.effectAllowed = 'move';
	}, []);

	const { startEdge, endEdge } = axis;
	// Horizontal terminal placeholders create room between side-by-side pills. Vertical
	// indicators can render at the top/bottom edge without consuming pill width.
	const shouldShowFirstDropPlaceholder =
		orientation === 'horizontal' && isFirst && closestEdge === startEdge;
	const shouldShowLastDropPlaceholder =
		orientation === 'horizontal' && isLast && closestEdge === endEdge;

	useEffect(() => {
		if (!dragBarRef.current) return;
		dragBarRef.current.style.opacity = isHovered ? '1' : '0';
	}, [isHovered]);

	useEffect(() => {
		if (!canDrag) {
			return;
		}
		const dropTargetEl = wrapperRef.current;
		if (!dropTargetEl) {
			return;
		}
		const targetInstanceId = externalInstanceId ?? scrollContainerRef.current;
		return combine(
			draggable({
				element: dropTargetEl,
				getInitialData: () => ({
					type: NATIVE_TAB_TYPE,
					index,
					instanceId: externalInstanceId ?? scrollContainerRef.current,
				}),
				onGenerateDragPreview: ({ nativeSetDragImage }) => {
					setCustomNativeDragPreview({
						getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
						render({ container }) {
							setDragState({ type: 'generate-preview', container });
						},
						nativeSetDragImage,
					});
				},
				onDragStart: () => {
					setDragState({ type: 'dragging' });
					if (wrapperRef.current) {
						wrapperRef.current.style.opacity = token('opacity.loading');
					}
				},
				onDrop: () => {
					setDragState(idle);
					if (wrapperRef.current) {
						wrapperRef.current.style.opacity = '';
					}
				},
			}),
			dropTargetForElements({
				element: dropTargetEl,
				canDrop: ({ source }) =>
					source.data.type === NATIVE_TAB_TYPE &&
					source.data.index !== index &&
					source.data.instanceId === targetInstanceId,
				getIsSticky: () => true,
				getData: ({ input, element: dropEl }) =>
					attachClosestEdge(
						{ type: NATIVE_TAB_TYPE, index, instanceId: targetInstanceId },
						{
							input,
							element: dropEl,
							allowedEdges: [axis.startEdge, axis.endEdge],
						},
					),
				onDrag: ({ self }) => setPillEdge(extractClosestEdge(self.data)),
				onDragLeave: () => setPillEdge(null),
				onDrop: () => setPillEdge(null),
			}),
		);
	}, [index, canDrag, axis, scrollContainerRef, externalInstanceId]);

	return (
		<>
			<Box
				ref={wrapperRef}
				xcss={styles.tabPillWrapper}
				testId="native-tabs-tab-pill-wrapper"
				onDragStart={handleDragStart}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onFocus={() => setIsHovered(true)}
				onBlur={() => setIsHovered(false)}
			>
				{shouldShowFirstDropPlaceholder && (
					<Box
						aria-hidden="true"
						xcss={styles.terminalDropPlaceholder}
						testId={`native-tabs-first-drop-placeholder-${tab.id}`}
					>
						<Box xcss={styles.terminalDropIndicatorAnchor}>
							<DropIndicator edge="left" gap={token('space.0')} />
						</Box>
					</Box>
				)}
				{canDrag && (
					<Pressable
						ref={dragBarRef}
						xcss={styles.dragBar}
						aria-hidden="true"
						tabIndex={-1}
						onMouseEnter={() => {
							setIsBarHovered(true);
							if (dragBarRef.current) {
								dragBarRef.current.style.width = '32px';
								dragBarRef.current.style.height = '16px';
								dragBarRef.current.style.backgroundColor = isActive
									? token('color.background.selected.hovered')
									: token('color.background.selected');
							}
						}}
						onFocus={() => {
							setIsBarHovered(true);
							if (dragBarRef.current) {
								dragBarRef.current.style.width = '32px';
								dragBarRef.current.style.height = '16px';
								dragBarRef.current.style.backgroundColor = isActive
									? token('color.background.selected.hovered')
									: token('color.background.selected');
							}
						}}
						onMouseLeave={() => {
							setIsBarHovered(false);
							if (dragBarRef.current) {
								dragBarRef.current.style.width = '24px';
								dragBarRef.current.style.height = '4px';
								dragBarRef.current.style.backgroundColor = token(
									'color.background.accent.gray.subtle',
								);
							}
						}}
						onBlur={() => {
							setIsBarHovered(false);
							if (dragBarRef.current) {
								dragBarRef.current.style.width = '24px';
								dragBarRef.current.style.height = '4px';
								dragBarRef.current.style.backgroundColor = token(
									'color.background.accent.gray.subtle',
								);
							}
						}}
					>
						{isBarHovered && <DragHandlerIcon label="" color="currentColor" />}
					</Pressable>
				)}
				<TabPill
					id={tab.id}
					title={tab.title}
					emoji={tab.emoji}
					emojiProvider={emojiProvider}
					isActive={isActive}
					tabIndex={isActive ? 0 : -1}
					ref={handleRef}
					onClick={handleClick}
					showMenu={showMenuButton}
					onEmojiClick={isActive && tab.emoji ? onEmojiPickerOpen : undefined}
					canRename={canRename}
					isEditing={editingTabId === tab.id}
					isDeletePreviewed={isDeletePreviewed}
					onTitleEditStart={() => onTitleEditStart(index)}
					onTitleEditSave={(title, options) => onTitleEditSave(index, title, options)}
					onTitleEditCancel={(options) => onTitleEditCancel(index, options)}
					color={optimisticColor}
					orientation={orientation}
				/>
				{showMenuButton && (
					<Box
						ref={handleMenuRef}
						xcss={cx(optimisticColor ? styles.menuTriggerFusedColored : styles.menuTriggerFused)}
					>
						<TabPillMenu
							scrollContainerRef={scrollContainerRef}
							hasEmoji={Boolean(tab.emoji)}
							onEmojiPickerOpen={onEmojiPickerOpen}
							onRemoveEmoji={onRemoveEmoji}
							isEmojiPickerOpen={isEmojiPickerOpen}
							onEmojiPickerClose={onEmojiPickerClose}
							emojiPickerContent={emojiPickerContent}
							onMoveTabPrevious={onMoveTabPrevious}
							onMoveTabNext={onMoveTabNext}
							onCopyLink={onCopyLink}
							onDeleteTab={() => onDeleteTab?.(index)}
							isDeleteDisabled={!onDeleteTab || (isFirst && isLast)}
							onDeletePreviewStart={() => onDeletePreviewStart(index)}
							onDeletePreviewEnd={onDeletePreviewEnd}
							isDeletePreviewed={isDeletePreviewed}
							isFirst={isFirst}
							isLast={isLast}
							tabId={tab.id}
							onColorChange={(color) => {
								setOptimisticColor(color);
								onColorChange?.(color);
							}}
							currentColor={optimisticColor}
							applyToAll={applyToAll}
							onApplyToAllChange={onApplyToAllChange}
							orientation={orientation}
						/>
					</Box>
				)}
				{shouldShowLastDropPlaceholder && (
					<Box
						aria-hidden="true"
						xcss={styles.terminalDropPlaceholder}
						testId={`native-tabs-last-drop-placeholder-${tab.id}`}
					>
						<Box xcss={styles.terminalDropIndicatorAnchor}>
							<DropIndicator edge="left" gap={token('space.0')} />
						</Box>
					</Box>
				)}
				{closestEdge && !shouldShowFirstDropPlaceholder && !shouldShowLastDropPlaceholder && (
					<DropIndicator edge={closestEdge} gap={token('space.100')} />
				)}
			</Box>
			{dragState.type === 'generate-preview'
				? ReactDOM.createPortal(<TabDragPreview title={tab.title} />, dragState.container)
				: null}
		</>
	);
};

/**
 * `TabBar` — a presentational tab strip for the Native Tabs editor extension.
 *
 * Renders a horizontal `role="tablist"` row of `TabPill`s plus an optional ADS
 * `IconButton` ("Add tab") shown only when `canEdit: true`. Owns the
 * roving-tabindex keyboard navigation (ArrowLeft / ArrowRight wrap, Home / End
 * jump). The Add tab button sits outside the `role="tablist"` element so it is
 * not part of the tablist's roving-tabindex group.
 */
export const TabBar: {
	({
		tabs,
		activeIndex,
		onTabClick,
		onAddTab,
		onRenameTab,
		canEdit,
		orientation,
		onReorderTabs,
		onMoveTabPrevious,
		onMoveTabNext,
		onCopyLink,
		onDeleteTab,
		onEmojiPickerOpen,
		onRemoveEmoji,
		isEmojiPickerOpen,
		onEmojiPickerClose,
		emojiPickerContent,
		emojiProvider,
		onColorChange,
		applyToAll,
		onApplyToAllChange,
		contentAreaDrop,
		instanceId,
		registerPillWrapper,
	}: TabBarProps): React.JSX.Element;
	displayName: string;
} = ({
	tabs,
	activeIndex,
	onTabClick,
	onAddTab,
	onRenameTab,
	canEdit,
	orientation = 'horizontal',
	onReorderTabs,
	onMoveTabPrevious,
	onMoveTabNext,
	onCopyLink,
	onDeleteTab,
	onEmojiPickerOpen,
	onRemoveEmoji,
	isEmojiPickerOpen = false,
	onEmojiPickerClose,
	emojiPickerContent = null,
	emojiProvider,
	onColorChange,
	applyToAll,
	onApplyToAllChange,
	contentAreaDrop,
	instanceId,
	registerPillWrapper,
}: TabBarProps): React.JSX.Element => {
	const axis = getTabAxis(orientation);
	const { formatMessage } = useIntl();
	const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const tabMenuRefs = useRef<Array<HTMLDivElement | null>>([]);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const canDrag = canEdit && tabs.length > 1;
	const [editingTabId, setEditingTabId] = useState<string | null>(null);
	const [deletePreviewIndex, setDeletePreviewIndex] = useState<number | null>(null);
	// Deleting is applied by the parent editor hook, so focus must wait for the updated tabs.
	const pendingDeleteFocusRef = useRef<{
		index: number;
		expectedTabsLength: number;
	} | null>(null);
	const registerRef = useCallback((index: number, element: HTMLButtonElement | null) => {
		tabRefs.current[index] = element;
	}, []);

	const registerMenuRef = useCallback((index: number, element: HTMLDivElement | null) => {
		tabMenuRefs.current[index] = element;
	}, []);

	const focusTab = useCallback(
		(nextIndex: number, currentIndex: number) => {
			if (nextIndex === currentIndex) {
				return;
			}
			onTabClick(nextIndex);
			tabRefs.current[nextIndex]?.focus();
		},
		[onTabClick],
	);

	const restoreTabFocus = useCallback((index: number, focusVisible = true) => {
		setTimeout(() => {
			// focusVisible: false keeps focus on the tab without a focus ring after a mouse-started
			// rename. Browsers that don't support the option fall back to their default behaviour.
			tabRefs.current[index]?.focus({ focusVisible } as FocusOptions);
		}, 0);
	}, []);

	const captureScrollOffset = useCallback(
		() => scrollContainerRef.current?.[axis.scrollPos],
		[axis],
	);

	const restoreScrollOffset = useCallback(
		(scrollOffset: number | undefined) => {
			if (scrollOffset === undefined) {
				return;
			}
			const restore = () => {
				const el = scrollContainerRef.current;
				if (el) {
					el[axis.scrollPos] = scrollOffset;
				}
			};
			if (typeof requestAnimationFrame !== 'function') {
				restore();
				return;
			}
			requestAnimationFrame(restore);
		},
		[axis],
	);

	const handleTitleEditStart = useCallback(
		(index: number) => {
			const tab = tabs[index];
			if (!canEdit || !tab || index !== activeIndex) {
				return;
			}
			setEditingTabId(tab.id);
		},
		[activeIndex, canEdit, tabs],
	);

	const handleTitleEditSave = useCallback(
		(
			index: number,
			title: string,
			{
				shouldRestoreFocus = false,
				focusVisible = true,
			}: { shouldRestoreFocus?: boolean; focusVisible?: boolean } = {},
		) => {
			const trimmedTitle = title.trim();
			const scrollOffset = captureScrollOffset();
			if (trimmedTitle && canEdit && onRenameTab && trimmedTitle !== tabs[index]?.title) {
				onRenameTab(index, trimmedTitle);
			}
			setEditingTabId(null);
			if (shouldRestoreFocus) {
				restoreTabFocus(index, focusVisible);
			}
			restoreScrollOffset(scrollOffset);
		},
		[canEdit, captureScrollOffset, onRenameTab, restoreScrollOffset, restoreTabFocus, tabs],
	);

	const handleTitleEditCancel = useCallback(
		(index: number, { focusVisible = true }: { focusVisible?: boolean } = {}) => {
			setEditingTabId(null);
			restoreTabFocus(index, focusVisible);
		},
		[restoreTabFocus],
	);

	const handleDeletePreviewStart = useCallback((index: number) => {
		setDeletePreviewIndex(index);
	}, []);

	const handleDeletePreviewEnd = useCallback(() => {
		setDeletePreviewIndex(null);
	}, []);

	useEffect(() => {
		const pendingDeleteFocus = pendingDeleteFocusRef.current;
		// Avoid focusing a stale pre-delete tab ref before the parent re-renders the shorter list.
		if (!pendingDeleteFocus || tabs.length !== pendingDeleteFocus.expectedTabsLength) {
			return;
		}
		const focusIndex = Math.min(pendingDeleteFocus.index, tabs.length - 1);
		if (focusIndex < 0 || activeIndex !== focusIndex) {
			return;
		}
		tabRefs.current[focusIndex]?.focus();
		pendingDeleteFocusRef.current = null;
	}, [activeIndex, tabs.length]);

	useEffect(() => {
		let innerRafId: number | undefined;
		// The first frame lets the appended tab update overflow state; the second
		// lets newly rendered scroll buttons shrink the tab-strip viewport before
		// we scroll the active tab into view.
		const outerRafId = requestAnimationFrame(() => {
			innerRafId = requestAnimationFrame(() => {
				const scrollContainer = scrollContainerRef.current;
				const activeTab = tabRefs.current[activeIndex];
				if (!scrollContainer || !activeTab) {
					return;
				}
				const scrollContainerRect = scrollContainer.getBoundingClientRect();
				const activeTabRect = activeTab.getBoundingClientRect();
				const endTarget = tabMenuRefs.current[activeIndex] ?? activeTab;
				const endTargetRect = endTarget.getBoundingClientRect();
				const startDelta = activeTabRect[axis.rectStart] - scrollContainerRect[axis.rectStart];
				const endDelta = endTargetRect[axis.rectEnd] - scrollContainerRect[axis.rectEnd];
				if (startDelta < 0) {
					scrollContainer.scrollTo({
						[axis.scrollKey]: Math.max(
							0,
							scrollContainer[axis.scrollPos] + startDelta - axis.scrollPadding,
						),
						behavior: 'smooth',
					});
				} else if (endDelta > 0) {
					scrollContainer.scrollTo({
						[axis.scrollKey]: scrollContainer[axis.scrollPos] + endDelta + axis.scrollPadding,
						behavior: 'smooth',
					});
				}
			});
		});
		return () => {
			cancelAnimationFrame(outerRafId);
			if (innerRafId !== undefined) {
				cancelAnimationFrame(innerRafId);
			}
		};
	}, [activeIndex, axis, tabs.length]);

	const handleDeleteTab = useCallback(
		(index: number) => {
			if (!onDeleteTab || index < 0 || index >= tabs.length || tabs.length <= 1) {
				return;
			}
			pendingDeleteFocusRef.current = {
				index: Math.min(index, tabs.length - 2),
				expectedTabsLength: tabs.length - 1,
			};
			handleDeletePreviewEnd();
			onDeleteTab(index);
		},
		[handleDeletePreviewEnd, onDeleteTab, tabs.length],
	);

	const handleReorderKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (!canEdit || tabs.length === 0 || !event.ctrlKey || !event.altKey || event.shiftKey) {
				return;
			}

			const moveTab =
				event.key === axis.previousKey
					? onMoveTabPrevious
					: event.key === axis.nextKey
						? onMoveTabNext
						: undefined;
			if (!moveTab) {
				return;
			}

			// Capture reorder shortcuts before the title input stops propagation.
			// Keep unmodified arrows in the input for normal caret movement.
			event.preventDefault();
			event.stopPropagation();
			moveTab();
		},
		[axis, canEdit, onMoveTabPrevious, onMoveTabNext, tabs.length],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (tabs.length === 0 || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
				return;
			}

			const { previousKey, nextKey } = axis;
			let nextIndex: number | null = null;

			if (event.key === nextKey) {
				nextIndex = (activeIndex + 1) % tabs.length;
			} else if (event.key === previousKey) {
				nextIndex = (activeIndex - 1 + tabs.length) % tabs.length;
			} else if (event.key === 'Home') {
				nextIndex = 0;
			} else if (event.key === 'End') {
				nextIndex = tabs.length - 1;
			} else {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			focusTab(nextIndex, activeIndex);
		},
		[activeIndex, tabs.length, focusTab, axis],
	);

	const [isDragging, setIsDragging] = useState(false);

	const [scrollState, setScrollState] = useState({
		hasOverflow: false,
		canScrollBackward: false,
		canScrollForward: false,
	});

	const updateScrollState = useCallback(() => {
		const el = scrollContainerRef.current;
		if (!el) {
			return;
		}
		const scrollPosition = el[axis.scrollPos];
		const scrollSize = el[axis.scrollSize];
		const clientSize = el[axis.clientSize];
		setScrollState({
			hasOverflow: scrollSize > clientSize,
			canScrollBackward: scrollPosition > 0,
			// Use Math.ceil to handle sub-pixel rounding — scroll offsets can be floats.
			canScrollForward: Math.ceil(scrollPosition) < scrollSize - clientSize,
		});
	}, [axis]);

	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!el) {
			return;
		}
		updateScrollState();
		let rafId: number | undefined;
		const handleScroll = () => {
			if (rafId !== undefined) {
				cancelAnimationFrame(rafId);
			}
			rafId = requestAnimationFrame(updateScrollState);
		};
		el.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			el.removeEventListener('scroll', handleScroll);
			if (rafId !== undefined) {
				cancelAnimationFrame(rafId);
			}
		};
	}, [updateScrollState, tabs.length]);

	const handleScrollPrevious = useCallback(() => {
		const el = scrollContainerRef.current;
		if (!el) {
			return;
		}
		el.scrollTo?.({
			[axis.scrollKey]: el[axis.scrollPos] - el[axis.clientSize],
			behavior: 'smooth',
		});
	}, [axis]);

	const handleScrollNext = useCallback(() => {
		const el = scrollContainerRef.current;
		if (!el) {
			return;
		}
		el.scrollTo?.({
			[axis.scrollKey]: el[axis.scrollPos] + el[axis.clientSize],
			behavior: 'smooth',
		});
	}, [axis]);

	useEffect(() => {
		const scrollEl = scrollContainerRef.current;
		if (!scrollEl || !canEdit || !onReorderTabs) {
			return;
		}
		return combine(
			monitorForElements({
				canMonitor: ({ source }) =>
					source.data.type === NATIVE_TAB_TYPE &&
					source.data.instanceId === (instanceId ?? scrollContainerRef.current),
				onDragStart: () => {
					setIsDragging(true);
				},
				onDrop: ({ source, location }) => {
					setIsDragging(false);
					const currentInstanceId = instanceId ?? scrollContainerRef.current;
					const target = location.current.dropTargets.find(
						(dropTarget) =>
							dropTarget.data.type === NATIVE_TAB_TYPE &&
							typeof dropTarget.data.index === 'number' &&
							dropTarget.data.instanceId === currentInstanceId,
					);
					if (!target) {
						return;
					}
					const sourceIndex = source.data.index as number;
					const targetIndex = target.data.index as number;
					const edge = extractClosestEdge(target.data);
					if (edge) {
						const scrollOffset = captureScrollOffset();
						onReorderTabs(sourceIndex, targetIndex, edge);
						restoreScrollOffset(scrollOffset);
					}
				},
			}),
			autoScrollForElements({
				element: scrollEl,
				canScroll: ({ source }) =>
					source.data.type === NATIVE_TAB_TYPE &&
					source.data.instanceId === (instanceId ?? scrollContainerRef.current),
			}),
		);
	}, [canEdit, captureScrollOffset, onReorderTabs, restoreScrollOffset, instanceId]);

	const showMenuButton = canEdit && tabs.length > 0;
	const ownedTabIds = tabs.map((tab) => tab.id).join(' ');
	const activeEditingTabId = tabs[activeIndex]?.id === editingTabId ? editingTabId : null;

	return (
		<Box xcss={cx(styles.tabBarRow, orientation === 'vertical' && styles.tabBarColumn)}>
			{/*
			 * The hidden div carries role="tablist" + aria-owns so screen readers
			 * understand the tab structure. The visible layout below has no ARIA
			 * role, allowing the split-button TabPillMenu trigger to sit as a
			 * sibling of each TabPill without violating aria-required-children.
			 * Keyboard navigation is handled by the Box wrapper (onKeyDown on
			 * Box bubbles from tab button descendants).
			 */}
			<Box
				xcss={styles.srOnly}
				role="tablist"
				aria-label={formatMessage(i18n.tablistLabel)}
				aria-orientation={orientation}
				aria-owns={ownedTabIds || undefined}
				testId="native-tabs-tab-bar"
			/>
			{/* contentEditable={false} prevents ProseMirror from intercepting keyboard events inside the tab bar */}
			<Box
				xcss={cx(
					styles.scrollContainerWrapper,
					orientation === 'vertical' && styles.scrollContainerWrapperVertical,
				)}
			>
				{orientation === 'horizontal' && scrollState.canScrollBackward && !isDragging && (
					<Box
						xcss={styles.scrollFadeLeft}
						aria-hidden="true"
						testId="native-tabs-scroll-fade-left"
					/>
				)}
				{orientation === 'vertical' && scrollState.canScrollBackward && !isDragging && (
					<Box
						xcss={styles.scrollFadeTop}
						aria-hidden="true"
						testId="native-tabs-scroll-fade-top"
					/>
				)}
				<Box
					ref={scrollContainerRef}
					xcss={cx(
						styles.scrollContainer,
						orientation === 'vertical' && styles.scrollContainerVertical,
					)}
					as="div"
					testId="native-tabs-scroll-container"
					{...{ contentEditable: false }}
				>
					<Box
						onKeyDownCapture={handleReorderKeyDown}
						onKeyDown={handleKeyDown}
						tabIndex={-1}
						xcss={styles.overflowVisible}
					>
						<Box xcss={cx(styles.tabItems, orientation === 'vertical' && styles.tabItemsVertical)}>
							{tabs.map((tab, index) => {
								const isActive = index === activeIndex;
								const showPillMenuButton = isActive && showMenuButton;
								const isDeletePreviewed = deletePreviewIndex === index;
								return (
									<Box
										key={tab.id}
										xcss={cx(
											styles.tabPillItem,
											orientation === 'vertical' && styles.tabPillItemVertical,
										)}
									>
										<TabBarItem
											tab={tab}
											index={index}
											isActive={isActive}
											showMenuButton={showPillMenuButton}
											canDrag={canDrag}
											orientation={orientation}
											registerRef={registerRef}
											registerMenuRef={registerMenuRef}
											onSelect={onTabClick}
											onEmojiPickerOpen={onEmojiPickerOpen}
											onRemoveEmoji={onRemoveEmoji}
											isEmojiPickerOpen={isEmojiPickerOpen}
											onEmojiPickerClose={onEmojiPickerClose}
											emojiPickerContent={emojiPickerContent}
											emojiProvider={emojiProvider}
											onMoveTabPrevious={onMoveTabPrevious}
											onMoveTabNext={onMoveTabNext}
											onCopyLink={onCopyLink}
											onDeleteTab={handleDeleteTab}
											canRename={isActive && canEdit}
											editingTabId={activeEditingTabId}
											onTitleEditStart={handleTitleEditStart}
											onTitleEditSave={handleTitleEditSave}
											onTitleEditCancel={handleTitleEditCancel}
											isFirst={index === 0}
											isLast={index === tabs.length - 1}
											isDeletePreviewed={isDeletePreviewed}
											onDeletePreviewStart={handleDeletePreviewStart}
											onDeletePreviewEnd={handleDeletePreviewEnd}
											scrollContainerRef={scrollContainerRef}
											externalInstanceId={instanceId}
											registerPillWrapper={registerPillWrapper}
											onColorChange={onColorChange}
											applyToAll={applyToAll}
											onApplyToAllChange={onApplyToAllChange}
											contentAreaEdge={
												contentAreaDrop?.index === index ? contentAreaDrop.edge : null
											}
										/>
									</Box>
								);
							})}
						</Box>
					</Box>
					{/* WidthObserver retains its initial callback; reset it when the measurement axis changes. */}
					<WidthObserver key={orientation} setWidth={updateScrollState} offscreen />
				</Box>
				{orientation === 'horizontal' && scrollState.canScrollForward && !isDragging && (
					<Box xcss={styles.scrollFadeRight} aria-hidden="true" testId="native-tabs-scroll-fade" />
				)}
				{orientation === 'vertical' && scrollState.canScrollForward && !isDragging && (
					<Box
						xcss={styles.scrollFadeBottom}
						aria-hidden="true"
						testId="native-tabs-scroll-fade-bottom"
					/>
				)}
			</Box>
			{canEdit && onAddTab ? (
				<Box
					xcss={cx(styles.addTabButton, orientation === 'vertical' && styles.addTabButtonVertical)}
				>
					<IconButton
						appearance="subtle"
						icon={AddIcon}
						label={formatMessage(i18n.addTab)}
						isTooltipDisabled={false}
						onClick={onAddTab}
					/>
				</Box>
			) : null}
			{orientation === 'horizontal' && scrollState.hasOverflow && !isDragging && (
				<>
					<Box xcss={styles.navButton}>
						<IconButton
							appearance="subtle"
							icon={ChevronLeftIcon}
							label={formatMessage(i18n.scrollTabsLeft)}
							isDisabled={!scrollState.canScrollBackward}
							onClick={handleScrollPrevious}
						/>
					</Box>
					<Box xcss={styles.navButton}>
						<IconButton
							appearance="subtle"
							icon={ChevronRightIcon}
							label={formatMessage(i18n.scrollTabsRight)}
							isDisabled={!scrollState.canScrollForward}
							onClick={handleScrollNext}
						/>
					</Box>
				</>
			)}
			{orientation === 'vertical' && scrollState.hasOverflow && !isDragging && (
				<Box xcss={styles.verticalNavGroup}>
					<IconButton
						appearance="subtle"
						icon={ChevronUpIcon}
						label={formatMessage(i18n.scrollTabsUp)}
						isDisabled={!scrollState.canScrollBackward}
						onClick={handleScrollPrevious}
					/>
					<IconButton
						appearance="subtle"
						icon={ChevronDownIcon}
						label={formatMessage(i18n.scrollTabsDown)}
						isDisabled={!scrollState.canScrollForward}
						onClick={handleScrollNext}
					/>
				</Box>
			)}
		</Box>
	);
};

TabBar.displayName = 'TabBar';
